import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { TopBarComponent } from 'src/app/top-bar/top-bar.component';
import { NgxEditorComponent, NgxEditorMenuComponent, Editor, Toolbar } from 'ngx-editor';
import { ToastrService } from 'ngx-toastr';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { EmailTemplateService } from 'src/app/services/EmailTemplate.service';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { VendorService } from 'src/app/services/Vendor.service';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { EActionTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-ven-email',
  standalone: true,
  imports: [TopBarComponent, SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, NgxEditorComponent, NgxEditorMenuComponent, UserPermissionHandlerDirective, WordReplacePipe, LoaderComponent],
  providers: [FilterService],
  templateUrl: './ops-ven-email.html',
  styleUrl: './ops-ven-email.scss'
})

export class OpsVenEmail implements OnInit, OnDestroy {

  @ViewChild('gridVendorEmail', { static: false }) public gridVendorEmail: GridComponent;
  private destroy$ = new Subject<void>();
  loaderIcon: Promise<boolean>;
  html = '';
  editor: Editor;
  toolbar: Toolbar = [
    // default value
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    // or, set options for link:
    //[{ link: { showOpenInNewTab: false } }, 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['horizontal_rule', 'format_clear', 'indent', 'outdent'],
    ['superscript', 'subscript'],
    ['undo', 'redo'],
  ];
  public selectionSettings = {
    type: 'Multiple',
    checkboxOnly: true,
    persistSelection: true
  };

  isCollapsed: boolean = true;
  private filterCleared = false;
  EmailTemplateList = [];
  vendorEmailList: any[] = [];
  filteredVendorList: any[] = [];
  selectedVendorFilter: string = 'current';
  EmailTemplateForm: FormGroup;
  EmailTemplateDetails: any;
  selectedVendors: any[] = [];
  vendorTerm: string = 'Vendor';
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.VendorEmail, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.VendorEmail, Action: EActionTypes.CREATE };

  constructor(
    private vendorService: VendorService,
    private EmailTemplateService: EmailTemplateService,
    private fb: FormBuilder,
    private wordReplacePipe: WordReplacePipe,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.editor = new Editor();
    this.initForm();
    this.vendorTerm = this.wordReplacePipe.transform('Vendor');
    this.getTemplates();
    this.getVendorEmailList();
    this.EmailTemplateForm.get('UseEmailTemplate')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(checked => {
      const templateCtrl = this.EmailTemplateForm.get('SelectedTemplateId');
      if (checked) {
        templateCtrl?.enable();
      } else {
        templateCtrl?.disable();
        this.EmailTemplateForm.patchValue({
          SelectedTemplateId: null,
          Id: 0,
          TemplateContent: '',
          Subject: '',
          TemplateName: '',
          Type: '',
          BaseTemplateType: 0
        });
      }
    });
  }

  initForm(): void {
    this.EmailTemplateForm = this.fb.group({
      UseEmailTemplate: [false],
      SelectedTemplateId: [{ value: null, disabled: true }],
      Id: [0],
      BaseTemplateType: [0],
      Type: ['',],
      TemplateName: [''],
      TemplateContent: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(10000)]],
      Subject: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(220)]],
      AttachmentFile: [null],
      AttachmentFileName: [''],
      FileType: ['']
    });
  }

  getTemplates() {
    this.EmailTemplateService.GetAllEmailTemplateList().pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.EmailTemplateList = res.filter((el: any) => el.BaseTemplateName === 'Vendor Email Blast')
            .map((el: any) => ({
              EmailTemplateId: el.EmailTemplateId,
              TemplateName: el.TemaplateName,
              BaseTemplateId: el.BaseTemplateId,
              BaseTemplate: el.BaseTemplateName,
            }));
        },
        error: (err) => {
          console.error('Failed to load Email Templates:', err);
        }
      });
  }

  getVendorEmailList(): void {
    this.vendorService.getVendorEmailList().pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.vendorEmailList = res ?? [];
          this.applyVendorFilter('current');
        },
        error: (err) => {
          console.error(`Failed to load ${this.vendorTerm} list:`, err);
        }
      });
  }

  getEmailTemplateById(id: number) {
    this.loaderIcon = Promise.resolve(true);
    this.EmailTemplateService.GetEmailTemplateById(id).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.EmailTemplateDetails = res.body;
          if (res) {
            this.EmailTemplateForm.patchValue({
              Id: this.EmailTemplateDetails.EmailTemplateId ?? 0,
              TemplateName: this.EmailTemplateDetails.TemplateName ?? '',
              Type: this.EmailTemplateDetails.Type ?? '',
              BaseTemplateType: this.EmailTemplateDetails.BaseTemplateId ?? 0,
              TemplateContent: this.EmailTemplateDetails.TemplateContent ?? '',
              Subject: this.EmailTemplateDetails.Subject ?? ''
            });
          }
          this.loaderIcon = Promise.resolve(false);
        }, error: (err) => {
          this.loaderIcon = Promise.resolve(false);
          console.error('Failed to get Email Template:', err);
        }
      });
  }

  onRowSelected(args: any): void {
    setTimeout(() => this.syncSelectedVendorIds());
  }

  onRowDeselected(args: any): void {
    setTimeout(() => this.syncSelectedVendorIds());
  }

  private syncSelectedVendorIds(): void {
    if (!this.gridVendorEmail) return;
    const records = this.gridVendorEmail.getSelectedRecords() as any[];
    this.selectedVendors = [...records];
  }

  onTemplateChange(event: Event): void {
    const id = this.EmailTemplateForm.get('SelectedTemplateId')?.value;
    if (id) {
      this.getEmailTemplateById(id);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: 'Invalid File',
          text: 'Only JPG, JPEG, PNG and PDF files are allowed.',
          icon: 'warning'
        });
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64Result = reader.result as string;
        const base64Content = base64Result.split(',')[1];
        const baseName = file.name.replace(/\.[^/.]+$/, "");

        this.EmailTemplateForm.patchValue({
          AttachmentFile: base64Content,
          AttachmentFileName: baseName,
          FileType: file.type
        });
      };
      reader.readAsDataURL(file);
    }
  }

  sendVendorEmail(): void {
    if (!this.selectedVendors.length) {
      this.toastr.info(`Please select at least one ${this.vendorTerm} to send an email`, 'Info', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const subject = (this.EmailTemplateForm.value.Subject || '').trim();
    const rawContent = this.EmailTemplateForm.value.TemplateContent || '';
    const content = this.getPlainText(rawContent);

    if (!subject || !content) {
      let message = '';
      if (!subject && !content) {
        message = 'Subject and Template Content cannot be empty';
      }
      else if (!subject) {
        message = 'Subject cannot be empty';
      }
      else {
        message = 'Template Content cannot be empty';
      }
      this.EmailTemplateForm.get('Subject')?.markAsTouched();
      this.EmailTemplateForm.get('TemplateContent')?.markAsTouched();
      this.toastr.info(message, 'Info', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    if (subject.length < 2) {
      this.toastr.warning('Subject is too short', 'Warning', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    if (content.length < 2) {
      this.toastr.warning('Template Content is too short', 'Warning', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    if (this.EmailTemplateForm.invalid) {
      if (this.EmailTemplateForm.controls['TemplateContent'].hasError('maxlength')) {
        this.toastr.warning('Email content cannot exceed 10,000 characters.', $localize`Warning`, this.sharedHTMLSettings.ToasterConfig)
        this.loaderIcon = Promise.resolve(false);
      } else {
        this.toastr.error('Please check the email details', $localize`Error`, this.sharedHTMLSettings.ToasterConfig)
        this.loaderIcon = Promise.resolve(false);
      }
      return;
    }

    const formData = this.EmailTemplateForm.getRawValue();
    const emailDetails = {
      TemplateId: formData.SelectedTemplateId,
      TemplateName: formData.TemplateName,
      Subject: formData.Subject,
      TemplateContent: formData.TemplateContent,
      AttachmentFile: formData.AttachmentFile,
      AttachmentFileName: formData.AttachmentFileName,
      FileType: formData.FileType
    };
    const vendorList = this.selectedVendors.map(v => ({
      VendorId: v.VendorId,
      VendorName: v.VendorName,
      VendorEmail: v.Email
    }));
    const payload = {
      EmailDetails: emailDetails,
      Vendors: vendorList
    };

    Swal.fire({
      title: 'Confirmation',
      text: 'Are you sure you want to send the email to the selected vendors?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Send',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.loaderIcon = Promise.resolve(true);
      this.vendorService.sendVendorEmails(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            if (res.Success) {
              this.toastr.success(
                `${this.vendorTerm} Emails sent successfully`,
                'Success',
                this.sharedHTMLSettings.ToasterConfig
              );
            } else {
              this.toastr.error(
                `Failed to send ${this.vendorTerm} Emails`,
                'Error',
                this.sharedHTMLSettings.ToasterConfig
              );
            }
            this.loaderIcon = Promise.resolve(false);
          },
          error: (err) => {
            console.error('Send Email Error:', err);
            this.loaderIcon = Promise.resolve(false);
          }
        });
    });
  }

  private getPlainText(html: string): string {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return (temp.textContent || temp.innerText || '').trim();
  }

  applyVendorFilter(filter: string): void {
    this.selectedVendorFilter = filter;
    if (filter === 'current') {
      this.filteredVendorList = this.vendorEmailList.filter(v => !v.IsActive);
    } else if (filter === 'inactive') {
      this.filteredVendorList = this.vendorEmailList.filter(v => v.IsActive);
    } else {
      this.filteredVendorList = [...this.vendorEmailList];
    }
  }

  onGridDataBound(): void {
    if (!this.gridVendorEmail) return;
    if (this.filterCleared) {
      this.fullyResetGridSelection();
      this.filterCleared = false;
    }
  }

  onGridActionBegin(args: any): void {
    if (args.requestType === 'filtering') {
      if (!args.currentFilterObject?.value) {
        this.filterCleared = true;
      }
    }

    if (args.requestType === 'searching') {
      if (!args.searchString) {
        this.filterCleared = true;
      }
    }
  }

  private fullyResetGridSelection(): void {
    if (!this.gridVendorEmail) {
      return;
    }
    this.gridVendorEmail.selectionSettings.persistSelection = false;
    const selectionModule = (this.gridVendorEmail as any).selectionModule;

    if (selectionModule) {
      if (typeof selectionModule.clearRowSelection === 'function') {
        selectionModule.clearRowSelection();
      }

      if (Array.isArray(selectionModule.selectedRowIndexes)) {
        selectionModule.selectedRowIndexes.length = 0;
      }
    }
    this.gridVendorEmail.clearSelection();
    this.selectedVendors = [];
    setTimeout(() => {
      this.gridVendorEmail.selectionSettings.persistSelection = true;
    });
  }

  closeVendorEmailPage(): void {
    this.isCollapsed = true;
    this.router.navigate(['/dashboard']);
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public created(args1) {
    this.gridVendorEmail.filterSettings = { mode: 'Immediate', immediateModeDelay: 100 };
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
